/**
 * Agent Authorization Hook
 *
 * This hook provides permission checking functions for agent management operations
 * based on the current user's role and claims.
 */

import { useContext } from 'react';
import AuthContext from '../contexts/AuthContext';
import { useDealerAdmin } from '../contexts/DealerAdminContext';

// Define role constants
const ROLES = {
  SUPERADMIN: 'superadmin',
  ADMIN: 'admin',
  TENANT_ADMIN: 'tenant_admin',
  USER: 'user',
};

/**
 * Hook for agent management authorization checks
 */
export const useAgentAuthorization = () => {
  const { currentUser, userClaims, userData } = useContext(AuthContext);
  const dealerAdmin = useDealerAdmin();

  // Get user's role from claims or Firestore user document
  const userRole = userClaims?.role || userData?.role || 'user';

  // Get user's tenant from claims or Firestore user document
  const userTenant = dealerAdmin.tenantId || userClaims?.tenantId || userData?.tenantId || '';

  const getAgentTenantId = agent =>
    agent?.tenantId || agent?.tenant_id || agent?.agentTenant || agent?.agentTenantId || '';

  /**
   * Check if user can access agent management
   */
  const canManageAgents = () => {
    if (!currentUser) return false;
    if (dealerAdmin.active) return true;
    return [ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.TENANT_ADMIN].includes(userRole);
  };

  /**
   * Check if user can create new agents
   * Tenant admins can create agents for their own tenant
   */
  const canCreateAgents = () => {
    if (!currentUser) return false;
    return userRole === ROLES.ADMIN || userRole === ROLES.SUPERADMIN || userRole === ROLES.TENANT_ADMIN || dealerAdmin.active;
  };

  /**
   * Check if user can edit a specific agent
   */
  const canEditAgent = agent => {
    if (!currentUser) return false;

    // Admins/superadmins can edit any agent
    if (userRole === ROLES.ADMIN || userRole === ROLES.SUPERADMIN) return true;

    // Tenant admins can only edit their own tenant's agents
    if (userRole === ROLES.TENANT_ADMIN || dealerAdmin.active) {
      return getAgentTenantId(agent) === userTenant;
    }

    return false;
  };

  /**
   * Check if user can delete a specific agent
   */
  const canDeleteAgent = agent => {
    if (!currentUser) return false;

    if (userRole === ROLES.ADMIN || userRole === ROLES.SUPERADMIN) return true;

    if (userRole === ROLES.TENANT_ADMIN || dealerAdmin.active) {
      return getAgentTenantId(agent) === userTenant;
    }

    return false;
  };

  /**
   * Check if user can restart a specific agent
   */
  const canRestartAgent = agent => {
    if (!currentUser) return false;

    // Admins/superadmins can restart any agent
    if (userRole === ROLES.ADMIN || userRole === ROLES.SUPERADMIN) return true;

    // Tenant admins can only restart their own tenant's agents
    if (userRole === ROLES.TENANT_ADMIN || dealerAdmin.active) {
      return getAgentTenantId(agent) === userTenant;
    }

    return false;
  };

  /**
   * Check if user can toggle status for a specific agent
   */
  const canToggleAgentStatus = agent => {
    if (!currentUser) return false;

    // Admins/superadmins can toggle any agent
    if (userRole === ROLES.ADMIN || userRole === ROLES.SUPERADMIN) return true;

    // Tenant admins can only toggle their own tenant's agents
    if (userRole === ROLES.TENANT_ADMIN || dealerAdmin.active) {
      return getAgentTenantId(agent) === userTenant;
    }

    return false;
  };

  /**
   * Get agents accessible to the current user
   * This allows filtering the agent list based on user's role
   */
  const getAccessibleAgents = agents => {
    if (!currentUser) return [];

    // Admins/superadmins can access all agents
    if (userRole === ROLES.ADMIN || userRole === ROLES.SUPERADMIN) return agents;

    // Tenant admins can only access their own tenant's agents
    if (userRole === ROLES.TENANT_ADMIN || dealerAdmin.active) {
      return agents.filter(agent => getAgentTenantId(agent) === userTenant);
    }

    return [];
  };

  return {
    userRole,
    userTenant,
    canManageAgents,
    canCreateAgents,
    canEditAgent,
    canDeleteAgent,
    canRestartAgent,
    canToggleAgentStatus,
    getAccessibleAgents,
  };
};

export default useAgentAuthorization;
